import { BylineProps } from '@/components/Byline';
import { Icon } from '@/components/Icon';
import { useAbTestVariant } from '@/hooks/useAbTestVariant';
import { StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { getCurrentBrand } from '@/utils/getCurrentBrand';
import { mergeProps } from '@/utils/merge';
import { ReactNode, useRef } from 'react';
import { FollowAuthor } from './FollowAuthor';

const FOLLOW_AUTHOR_EXPERIMENT_ID = 'byline-follow-button';

export interface StandaloneFollowAuthorProps extends StandaloneComponentProps<typeof FollowAuthor> {
  bylines?: BylineProps[];
  headline?: ReactNode;
  content?: ReactNode;
}

export const StandaloneFollowAuthor: StandaloneComponent<StandaloneFollowAuthorProps> = ({
  bylines,
  headline = 'Vi arbetar på något nytt!',
  content = (
    <>
      <p>Vi vill göra det enklare för dig att följa dina favoritskribenter och deras senaste innehåll.</p>
      <p>Funktionen finns inte än, men tack för ditt intresse!</p>
    </>
  ),
  ...props
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { resolvedVariant } = useAbTestVariant(FOLLOW_AUTHOR_EXPERIMENT_ID);

  if (resolvedVariant !== 'a') return null;

  const slugs = bylines
    ?.map((byline) => byline.slug)
    .filter(Boolean)
    .join(', ');

  return (
    <FollowAuthor {...props}>
      <FollowAuthor.Button
        {...mergeProps(
          {
            options: {
              onClick: () => dialogRef.current?.showModal(),
              'data-click-id': `${FOLLOW_AUTHOR_EXPERIMENT_ID}-${resolvedVariant}`,
              id: `${FOLLOW_AUTHOR_EXPERIMENT_ID}-${resolvedVariant}`,
              'data-am-track-box': '',
              'data-am-label-experiment-id': `${FOLLOW_AUTHOR_EXPERIMENT_ID}-${getCurrentBrand().slug}-se-v1`,
              'data-am-label-experiment-variant-id': `variant-${resolvedVariant}`,
              'data-am-label-element-type': 'button',
              'data-am-label-element-name': FOLLOW_AUTHOR_EXPERIMENT_ID,
              ...(slugs && { 'data-am-label-item-id': slugs }),
            },
            content: 'Följ',
          },
          { options: props?.options?.$button },
        )}
      />

      <FollowAuthor.Modal ref={dialogRef} onClick={() => dialogRef.current?.close()} aria-label="Följ skribenter">
        <FollowAuthor.Group onClick={(event) => event.stopPropagation()}>
          <FollowAuthor.CloseButton
            type="button"
            aria-label="Stäng dialogfönster"
            onClick={() => dialogRef.current?.close()}
          >
            <Icon name="close" options={{ size: 'medium' }} />
          </FollowAuthor.CloseButton>

          <FollowAuthor.Headline>{headline}</FollowAuthor.Headline>
          <FollowAuthor.Content>{content}</FollowAuthor.Content>
          <FollowAuthor.ModalButton content="Stäng" options={{ onClick: () => dialogRef.current?.close() }} />
        </FollowAuthor.Group>
      </FollowAuthor.Modal>
    </FollowAuthor>
  );
};
